/**
 * Cue strike model: map cue parameters (aim, power, tip offset, elevation) to the
 * struck ball's initial linear + angular velocity.
 *
 * At elevation 0 this is a level-cue hit: side spin is purely vertical (ωz) and
 * the ball travels flat — identical to the original model. As the cue elevates,
 * (a) some speed becomes an upward hop (jump shot) and (b) the side-spin axis
 * tilts from vertical toward the travel direction, giving an along-travel spin
 * component. That component produces lateral contact slip while the ball slides,
 * so friction curves the path — i.e. a massé.
 */
import type { CueParams } from '../types';
import type { PhysicsConstants } from '../config';
import type { Ball } from './model';

/** Max tip offset magnitude (fraction of radius) before a real cue would miscue. */
const MISCUE_LIMIT = 0.55;
/** Fallback hop fraction if no `jumpFactor` tunable is supplied (see PhysicsConstants). */
const DEFAULT_JUMP_FACTOR = 0.18;
/**
 * Cue elevations (rad) over which the jump ramps from none to full. A shallow
 * cue physically can't pop the ball off the cloth, so below JUMP_MIN_ELEV there
 * is no hop at all — this is what stops balls jumping on gentle / rail-clearance
 * tilt. Only a fairly elevated (deliberate) cue reaches a full jump.
 */
const JUMP_MIN_ELEV = 0.25; // ~14°
const JUMP_FULL_ELEV = 0.6; // ~34°
/**
 * Above this elevation the hop fades back out toward a steep massé: a near-vertical
 * cue presses DOWN into the ball (pinning it / making it curve) rather than popping
 * it off the cloth, so the jump shouldn't keep growing all the way to 90°. The hop
 * tapers from full at JUMP_FADE_ELEV down to JUMP_MASSE_FLOOR× at vertical.
 */
const JUMP_FADE_ELEV = 0.85; // ~49°
const JUMP_MASSE_FLOOR = 0.15; // residual hop fraction at a vertical (90°) massé
/**
 * Curve (swerve) is a MASSÉ-ONLY effect: below MASSE_MIN_ELEV the forward-tilted
 * spin component is exactly zero, so ordinary side English — even with a little
 * aiming tilt or a jump-clearance elevation — travels dead straight. Only a
 * deliberately steep cue (the massé regime, where a near-vertical cue presses down
 * into the ball) curves the path. ~46°.
 */
const MASSE_MIN_ELEV = 0.8;
/**
 * Exponent shaping how the massé (swerve) response grows once past the threshold:
 * the curve ramps from 0 at MASSE_MIN_ELEV to full at a vertical cue as this power,
 * so it eases in (no jump in behaviour at the threshold) and only a steep massé
 * swerves hard. 1 = linear ramp.
 */
const MASSE_TILT_EXPONENT = 2;

const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);

export function applyStrike(ball: Ball<string>, cue: CueParams<string>, c?: PhysicsConstants): void {
  const R = ball.radius;

  let a = cue.tipOffset.a;
  let b = cue.tipOffset.b;
  const offMag = Math.hypot(a, b);
  if (offMag > MISCUE_LIMIT) {
    const s = MISCUE_LIMIT / offMag;
    a *= s;
    b *= s;
  }

  const v0 = Math.max(0, cue.speed);
  // Cap at a vertical cue (90°): at theta = π/2 the launch is a pure massé (no
  // horizontal drive, max swerve). Past vertical has no extra physical meaning, so
  // an over-90° render angle still strikes as a full vertical massé.
  const theta = Math.max(0, Math.min(cue.elevation, Math.PI / 2));
  const cosT = Math.cos(theta);
  const sinT = Math.sin(theta);

  const dir = { x: Math.cos(cue.azimuth), y: Math.sin(cue.azimuth), z: 0 };
  const left = { x: -dir.y, y: dir.x, z: 0 }; // k x dir

  // horizontal speed drops with elevation; the remainder becomes a vertical hop,
  // ramped in with elevation so only a fairly elevated cue actually jumps.
  const vh = v0 * cosT;
  const jumpFactor = c?.jumpFactor ?? DEFAULT_JUMP_FACTOR;
  const jumpRampUp = clamp01((theta - JUMP_MIN_ELEV) / (JUMP_FULL_ELEV - JUMP_MIN_ELEV));
  // fade the hop back out toward a vertical massé (1× at JUMP_FADE_ELEV → floor at 90°)
  const masseFade = 1 - clamp01((theta - JUMP_FADE_ELEV) / (Math.PI / 2 - JUMP_FADE_ELEV)) * (1 - JUMP_MASSE_FLOOR);
  const jumpRamp = jumpRampUp * masseFade;
  ball.vel = { x: dir.x * vh, y: dir.y * vh, z: v0 * sinT * jumpFactor * jumpRamp };

  // follow(+)/draw(-) about `left`; scaled by followDrawScale to tame top/back spin
  const wTop = ((5 * b * v0) / (2 * R)) * (c?.followDrawScale ?? 1);
  // side English: positive a = right English → ωz > 0 (matches the spin pad and
  // the cue-tip contact side, so the throw / cushion / massé go the shown way).
  const wSide = (5 * a * v0) / (2 * R);
  // Side-spin axis = vertical (k) rotated toward travel (dir) by the elevation —
  // this is exactly r×J taken about the ball CENTRE (not the contact point), so
  // the launch direction stays on the aim line and only the spin tilts forward.
  // The vertical (ωz) part is ordinary side English (throw + cushion) and is kept
  // intact; only the forward-tilted part — the bit that makes the ball swerve —
  // is shaped by masseScale·sin^MASSE_TILT_EXPONENT so gentle tilts barely curve.
  // gate the swerve to the massé regime: zero below MASSE_MIN_ELEV, then ramp in
  const masseRamp = clamp01((theta - MASSE_MIN_ELEV) / (Math.PI / 2 - MASSE_MIN_ELEV));
  const masseGain = (c?.masseScale ?? 1) * Math.pow(masseRamp, MASSE_TILT_EXPONENT);
  const sideAxis = { x: masseGain * dir.x, y: masseGain * dir.y, z: cosT };

  ball.angVel = {
    x: left.x * wTop + wSide * sideAxis.x,
    y: left.y * wTop + wSide * sideAxis.y,
    z: wSide * sideAxis.z,
  };

  ball.state = v0 > 0 ? (ball.vel.z > 1e-4 ? 'airborne' : 'sliding') : 'stationary';
}
